import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  pn,
  SCHOOL_YEAR_ACTIVE_ID,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import {
  Permission,
  PermissionAction,
  StudentFilter as StudentFilterModel,
  StudentListDto,
  StudentListFilter,
  StudentListResource,
  TenantInfo,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { studentCreatePageRoute } from "../StudentCreatePage/StudentCreatePage";
import { StudentTable } from "./StudentTable";
import { StudentFilter } from "../StudentFilter";
import produce from "immer";
import { ListFooter } from "components";

const routeName = "students";

export const studentListPageRoute: PageRoute = {
  handle: "Student Profiles",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

const ROOT_PERMISSION: Permission = Permission.Student;

export const StudentListPage = () => {
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<StudentListFilter>({
    sortObjects: [{ property: pn<StudentListDto>("fullName"), isDesc: false }],
    studentFilter: {
      schoolYears: [SCHOOL_YEAR_ACTIVE_ID],
    },
  });

  const [initListFilter] = useState<StudentListFilter>(listFilter);
  const [listItems, setListItems] = useState<StudentListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StudentListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [tenantInfo, setTenantInfo] = useState<TenantInfo>();

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.students.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.students.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.textingConversations.getTenantTextingInfo().then((response) => {
      setTenantInfo(response);
    });
    ApiService.students.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<StudentListFilter>({
    maxWidth: "xl",
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <StudentFilter
        filter={listFilter.studentFilter || ({} as StudentFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.studentFilter = filter;
            })
          )
        }
      />
    ),
  });

  const getIdsMethods = () => ApiService.students.getIds(listFilter);

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{studentListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button onClick={() => navigate(studentCreatePageRoute.getPath())} variant="outlined">
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <StudentTable
              rootPermission={ROOT_PERMISSION}
              tenantTextingInfo={tenantInfo}
              tableContainerRef={tableContainerRef}
              students={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={() => getIdsMethods()}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider
        value={{
          loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
